import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../common/Button';
import NameFields from './NameFields';

interface NameModalDesktopProps {
  onSubmit: (firstName: string, lastName: string) => void | Promise<void>;
  onClose: () => void;
}

const NameModalDesktop = ({ onSubmit, onClose }: NameModalDesktopProps) => {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const firstNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  const isValid = firstName.trim().length > 0 && lastName.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid && !isLoading) {
      setIsLoading(true);
      try {
        await onSubmit(firstName.trim(), lastName.trim());
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isLoading) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={isLoading ? undefined : onClose}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-md rounded-2xl bg-surface-card p-8 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6 text-center">
          <h2 className="font-heading text-xl font-semibold text-foreground-primary">
            {t('chat.nameModal.title')}
          </h2>
          <p className="mt-2 font-body text-sm text-foreground-secondary">
            {t('chat.nameModal.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <NameFields
            variant="desktop"
            firstName={firstName}
            lastName={lastName}
            onFirstNameChange={setFirstName}
            onLastNameChange={setLastName}
            firstNameRef={firstNameRef}
            disabled={isLoading}
          />

          <Button
            type="submit"
            variant="solid"
            size="lg"
            disabled={!isValid}
            loading={isLoading}
            className="mt-2 w-full rounded-[10px] py-4"
          >
            {t('chat.nameModal.continue')}
          </Button>
        </form>
      </motion.div>
    </div>
  );
};

export default NameModalDesktop;
